<template>
  <div class="game-board-container">
    <div class="game-board-container__left">
      <section id="test-sentence" class="sentence">
        <sentence-explanation v-for="(element, index) in elementMap"
                              :key=element
                              :elementMap=element
                              :index=index
        />
      </section>

      <!-- Hidden timer — drives the progress bar via tick event -->
      <TimerView
        v-if="state === 'play'"
        style="display:none"
        :secs="startSeconds"
        :key="sentence"
        @timeIsUp="timeIsUp"
        @tick="onTick"
      />

      <!-- Progress band: line + counter + nav -->
      <GameProgressBar
        id="test-progress"
        :current="currentChallenge"
        :total="amountFixed"
        :remaining="timerRemaining"
        :totalSecs="state === 'play' ? startSeconds : null"
        :showBack="state === 'review' || state === 'pre-review'"
        :showHome="state === 'review'"
        :homeLabel="isReviewOnly ? 'Back to account' : 'Replay'"
        @back="back"
        @next="next"
        @home="isReviewOnly ? router.push({name: 'account'}) : replay()"
      />

      <!-- Review Menu -->
      <div class="menu menu--play" v-if="state==='review'">

        <template v-if="getAnswer()==='-'">
          <div class="menu-item-button menu-item-button--answer menu-item-button--skipped" aria-disabled="true">True</div>
          <div class="menu-item-button menu-item-button--answer menu-item-button--skipped" aria-disabled="true">False</div>
        </template>
        <template v-else>
          <div
            class="menu-item-button menu-item-button--answer"
            :class="reviewButtonClass(true)"
            :aria-label="reviewButtonAriaLabel(true)"
            aria-disabled="true"
          >True</div>
          <div
            class="menu-item-button menu-item-button--answer"
            :class="reviewButtonClass(false)"
            :aria-label="reviewButtonAriaLabel(false)"
            aria-disabled="true"
          >False</div>
        </template>

      </div>

      <!-- Playing Menu -->
      <div class="menu menu--play" v-if="state==='play'">

        <button id="test-answer-true" class="menu-item-button menu-item-button--answer" aria-label="True" @click="answer(true)">
          True
        </button>

        <button id="test-answer-false" class="menu-item-button menu-item-button--answer" aria-label="False" @click="answer(false)">
          False
        </button>

      </div>
    </div>

    <div class="game-board-container__right">
      <GameBoardView id="test-board" game-board-class="game-board" :is-valid="true"
                     :segments="segments" :board-key="currentChallenge" />
    </div>

    <Transition name="modal-fade">
      <div v-if="state==='pre-review' && !showShareSheet" class="modal-overlay">
        <div class="modal">
          <div class="modal-content">
            <p>Your score was {{ finalScore }} ({{ finalCorrectPct }}% correct).</p>
            <template v-if="!dareMode">
              <button class="black-button" @click="startReview()">Review</button>
              <button class="black-button" @click="replay()">Replay</button>
              <template v-if="savedRunId">
                <button class="black-button" @click="openDare()">Dare</button>
              </template>
            </template>
            <button class="black-button" @click="home()">Home</button>
          </div>
        </div>
      </div>
    </Transition>

    <ShareSheet
      v-if="showShareSheet && savedRunId"
      :url="`${origin}/#/dare/${savedRunId}`"
      @close="showShareSheet = false"
    />
  </div>

</template>

<script setup>
import GameBoardView from "@/components/parts/GameBoardView.vue";
import TimerView from "@/components/TimerView";
import GameProgressBar from "@/components/parts/GameProgressBar.vue";
import axios from 'axios';
import {onMounted, onUnmounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {FIXED_GRID_SIZE, updateGridSize} from "@/composables/challenges";
import {buildDisplayElementMap, displayValue} from "@/composables/sentences";
import SentenceExplanation from "@/components/parts/SentenceExplanation.vue";
import {useAuth} from "@/composables/useAuth";
import presets from "@/data/presets.json";
import ShareSheet from "@/components/parts/ShareSheet.vue";

const props = defineProps({
  injectedChallenges:   { type: Array,   default: null },
  dareTimePerChallenge: { type: Number,  default: null },
  dareComplexity:       { type: String,  default: 'easy' },
  dareMode:             { type: Boolean, default: false },
});
const emit = defineEmits(['dareComplete']);

let segments = ref([]);
let sentence = ref("Loading...");
let elementMap = ref(null);
let isLoaded = ref(false);
let startSeconds = ref(null);
let score = ref(0);
let solution = ref("");
let challengeList = ref([]);
let currentChallenge = ref(0);
let time = ref();
let state = ref("play");
let challengeAmount = 0;
const answers = [];
const challengeTimings = [];
let challengeStartTime = null;
let amountFixed = ref(0);
let finalScore = 0;
let finalCorrectPct = 0;
let loadedChallenges = null;
let isReviewOnly = ref(false);
let timerRemaining = ref(null);

function onTick({ remaining, total }) {
  timerRemaining.value = remaining;
  if (remaining === total) return; // first tick on mount, no need to reset
}


const router = useRouter();
const route = useRoute();
const { saveRun, fetchRun, shareDareRun } = useAuth();

const savedRunId = ref(null);
const showShareSheet = ref(false);
const origin = window.location.origin;

onMounted(async () => {
  //this should be done in HomeView
  await router.isReady();

  if (props.dareMode && props.injectedChallenges) {
    challengeAmount = props.injectedChallenges.length;
    amountFixed.value = challengeAmount;
    challengeList.value = props.injectedChallenges;
    startSeconds.value = props.dareTimePerChallenge;
    time.value = props.dareTimePerChallenge;
    updateGridSize();
    setChallenge();
    return;
  }

  const isReview = route.query.review === 'true';

  if (isReview) {
    isReviewOnly.value = true;
    const replayId = route.query.replayId;
    const run = await fetchRun(replayId);
    if (run?.challenges) {
      challengeAmount = run.challengeAmount;
      amountFixed.value = Number.parseInt(challengeAmount);
      challengeList.value = run.challenges;
      if (run.answers) {
        run.answers.forEach((ans, i) => { answers[i] = ans; });
      }
      updateGridSize();
      setChallenge();
      state.value = 'review';
    }
    return;
  }

  await loadChallenges();
})

async function loadChallenges() {
  startSeconds.value = Number.parseInt(route.query.time);
  time.value = route.query.time;
  const challengeType = route.query.challengeType;
  const complexity = route.query.complexity;
  challengeAmount = route.query.challengeAmount;
  const usePreset = route.query.preset === 'true';

  let challenges;
  if (usePreset) {
    updateGridSize();
    challenges = getPresetChallenges(complexity, challengeAmount);
  } else {
    challenges = await getChallenges(challengeType, challengeAmount, complexity);
  }
  // store for saveRun so generated runs can be replayed later
  loadedChallenges = challenges;

  if (challenges) {
    challengeList.value = challenges;
    setChallenge();
    amountFixed.value = Number.parseInt(challengeAmount);
  }
}


function isCorrect() {
  return answers[currentChallenge.value] === solution.value;
}

function getAnswer() {
  let ans = answers[currentChallenge.value];
  if (ans != null) {
    return ans;
  }
  return "-";
}

function reviewButtonClass(buttonValue) {
  const chosen = getAnswer() === buttonValue;
  const correct = isCorrect();
  if (!chosen) return 'menu-item-button--review-unchosen';
  return correct ? 'menu-item-button--review-correct' : 'menu-item-button--review-wrong';
}

function reviewButtonAriaLabel(buttonValue) {
  const chosen = getAnswer() === buttonValue;
  const label = buttonValue ? 'True' : 'False';
  if (!chosen) return label;
  return isCorrect() ? `${label} — correct` : `${label} — incorrect`;
}

function startReview() {
  currentChallenge.value = 0;
  setChallenge();
  state.value = "review";
}

function back() {
  const INDEX_START = 0;
  if (currentChallenge.value > INDEX_START) {
    currentChallenge.value--;
    setChallenge();
  }
}

function setChallenge() {
  isLoaded.value = true;
  const currentChal = challengeList.value[currentChallenge.value];
  segments.value = currentChal.challengeUniverse.segments;
  const currentSent = challengeList.value[currentChallenge.value].sentences[0];
  sentence.value = displayValue(currentSent);
  solution.value = currentSent.evaluation;
  elementMap.value = buildDisplayElementMap(currentSent);
  if (state.value !== 'review') {
    challengeStartTime = Date.now();
    timerRemaining.value = startSeconds.value;
  }
}

function next() {
  const INDEX_END = challengeAmount - 1;
  if (currentChallenge.value < INDEX_END) {
    currentChallenge.value++;
    if (state.value === "play") {
      startSeconds.value = Number.parseInt(time.value);
    }
    setChallenge();
  } else {
    state.value = "pre-review";
    time.value = 0;
  }
}

function timeIsUp() {
  challengeTimings[currentChallenge.value] = null;
  sentence.value = "Time is up...";
  time.value = 0;
  startSeconds.value = 0;
  const isLastChallenge = currentChallenge.value >= challengeAmount - 1;
  if (isLastChallenge) {
    finalScore = computeScore();
    finalCorrectPct = computeCorrectPct();
    if (!props.dareMode) {
      saveRun({
        complexity: route.query.complexity,
        challengeAmount: Number(challengeAmount),
        score: finalScore,
        correctPct: finalCorrectPct,
        timePerChallenge: Number(route.query.time),
        challengeType: route.query.challengeType || 'test',
        answers: [...answers],
        challenges: route.query.preset === 'true' ? null : (loadedChallenges || null),
      }).then(docRef => { if (docRef && loadedChallenges) savedRunId.value = docRef.id; }).catch(() => {});
    }
    if (props.dareMode) {
      emit('dareComplete', { score: finalScore, correctPct: finalCorrectPct });
    }
  }
  state.value = "pre-review";
}

function home() {
  router.push({name: 'home'});
}

async function replay() {
  answers.length = 0;
  challengeTimings.length = 0;
  currentChallenge.value = 0;
  score.value = 0;
  finalScore = 0;
  finalCorrectPct = 0;
  savedRunId.value = null;
  state.value = "play";
  await loadChallenges();
}

function answer(answer) {
  document.activeElement?.blur();
  const elapsed = challengeStartTime
    ? Math.round((Date.now() - challengeStartTime) / 1000)
    : (props.dareMode ? props.dareTimePerChallenge : Number(route.query.time));
  challengeTimings[currentChallenge.value] = elapsed;
  answers[currentChallenge.value] = answer;
  tallyScore();
  const isLastChallenge = currentChallenge.value >= challengeAmount - 1;
  next();
  if (isLastChallenge) {
    finalScore = computeScore();
    finalCorrectPct = computeCorrectPct();
    if (!props.dareMode) {
      saveRun({
        complexity: route.query.complexity,
        challengeAmount: Number(challengeAmount),
        score: finalScore,
        correctPct: finalCorrectPct,
        timePerChallenge: Number(route.query.time),
        challengeType: route.query.challengeType || 'test',
        answers: [...answers],
        // only store challenges for generated runs (not preset) so users can replay
        challenges: route.query.preset === 'true' ? null : (loadedChallenges || null),
      }).then(docRef => { if (docRef && loadedChallenges) savedRunId.value = docRef.id; }).catch(() => {});
    }
    if (props.dareMode) {
      emit('dareComplete', { score: finalScore, correctPct: finalCorrectPct });
    }
  }
}

async function openDare() {
  try {
    await shareDareRun(savedRunId.value);
  } catch { /* already shared or no-op */ }
  showShareSheet.value = true;
}

function tallyScore() {
  if (challengeList.value[currentChallenge.value].sentences[0].evaluation === answers[currentChallenge.value]) {
    score.value++;
  }
}

const COMPLEXITY_MULTIPLIER = {easy: 1.0, moderate: 1.4, hard: 2.0, extreme: 3.0};

function computeScore() {
  const timePerChallengeNum = props.dareMode ? props.dareTimePerChallenge : Number(route.query.time);
  const multiplier = COMPLEXITY_MULTIPLIER[props.dareMode ? props.dareComplexity : route.query.complexity] ?? 1.0;
  let total = 0;
  for (let i = 0; i < challengeAmount; i++) {
    const correct = challengeList.value[i].sentences[0].evaluation === answers[i];
    const elapsed = challengeTimings[i];
    if (elapsed === null || answers[i] === undefined) {
      // skipped — 0 points
    } else if (correct) {
      const speedBonus = Math.max(0, Math.round(50 * (1 - elapsed / timePerChallengeNum)));
      total += 100 + speedBonus;
    } else {
      total -= 25;
    }
  }
  return Math.max(0, Math.round((total / challengeAmount) * multiplier));
}

function computeCorrectPct() {
  return Math.round((score.value / challengeAmount) * 100);
}

function getPresetChallenges(complexity, amount) {
  const filtered = presets.filter(c => c.complexity === complexity);
  const pool = filtered.length > 0 ? filtered : presets;
  const result = [];
  for (let i = 0; i < amount; i++) {
    result.push(pool[i % pool.length]);
  }
  return result;
}

async function getChallenges(challengeType, challengeAmount, complexity) {
  updateGridSize();

  const url = `/challenge?type=${challengeType}&amount=${challengeAmount}&complexity=${complexity}&rowAmount=${FIXED_GRID_SIZE}&colAmount=${FIXED_GRID_SIZE}`;
  try {

    const response = await axios.get(url);
    return response.data;
  } catch (err) {
    // ignore
  }
}

function onResize() {
  updateGridSize();
}

onMounted(() => {
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});


</script>
